/**
 * The ONE place that decides what `registerStudioTools()` is called WITH.
 *
 * Generic Application Ontology Program — post-program wiring lane A1, seam 2.
 *
 * WHY A RESOLVER AND NOT AN ARGUMENT AT ONE CALL SITE. `registerStudioTools`
 * registers ONCE (`_registered`), and two independent paths race to be the one
 * that calls it: the bridge's tool-runtime bootstrap (`studio-bridge.ts`) and
 * the generation path's own `ensureStudioGenerationToolsRegistered()`
 * (`start-app-generation-from-prompt.ts`). Passing the artifact-persistence
 * port at only one of them means the port is present or absent depending on
 * WHICH PATH RAN FIRST — a registration that looks wired, and silently is not,
 * for exactly the flow (generation) that needs it. Both sites call this
 * function, so there is one answer.
 *
 * WHY THE PERSISTENCE PORT IS SERVER-SEAM-DERIVED AND ABSENT BY DEFAULT. The Task 4 gate
 * needs an admission ANSWER for a bound artifact, and the inputs that answer
 * is computed from — the series binding, the attempt binding, the
 * artifact-validation observation append, the verifier-issued eligibility —
 * are server-owned. A browser cannot hold them, and inventing them here would
 * be inventing the authority the whole program exists to keep out of the
 * browser. So the port is composed from an INSTALLED server seam, and with no
 * seam installed `resolveStudioToolRegistrationOptions()` returns an options
 * object with no `applicationArtifactPersistence` member at all — the exact
 * `applicationArtifactPersistencePort = null` state both call sites produced
 * before this module existed, byte for byte. The Library assistant is
 * different: its authority is the authenticated platform plan/critic routes,
 * so the resolver installs its lazy singleton by default for both Chat and Bot.
 */
import type { StudioLibraryAssistant } from '@/features/chat/services/studio-library-assistant'
import { resolveStudioLibraryAssistant } from '@/features/chat/services/studio-library-assistant-bootstrap'
import type { StudioApplicationArtifactPersistenceAdmissionPort } from './register-studio-tools'

export interface StudioToolRegistrationOptions {
  libraryAssistant?: StudioLibraryAssistant
  applicationArtifactPersistence?: StudioApplicationArtifactPersistenceAdmissionPort
}

/**
 * The server-owned seam. Structurally the port itself: everything the Task 4
 * gate asks of it is server-owned, so there is nothing for this module to add
 * on top — its job is to make the presence of the seam ONE decision rather
 * than two racing ones.
 */
export type StudioApplicationArtifactPersistenceServerSeam =
  StudioApplicationArtifactPersistenceAdmissionPort

let installedSeam: StudioApplicationArtifactPersistenceServerSeam | null = null

/**
 * Install the server-backed artifact-persistence seam.
 *
 * Called by whichever bootstrap owns the server-side admission surface. Until
 * that surface exists no production caller invokes this, and the gate stays
 * closed — which is the correct answer for an unconfigured deployment, not a
 * placeholder.
 */
export function installStudioApplicationArtifactPersistenceSeam(
  seam: StudioApplicationArtifactPersistenceServerSeam,
): void {
  installedSeam = seam
}

export function clearStudioApplicationArtifactPersistenceSeam(): void {
  installedSeam = null
}

export function resolveStudioApplicationArtifactPersistencePort(): StudioApplicationArtifactPersistenceAdmissionPort | null {
  return installedSeam
}

/**
 * The options object both registration sites pass.
 *
 * The persistence member is OMITTED rather than set to `undefined` when no
 * seam is installed. The Library member defaults to the platform-backed lazy
 * singleton; an explicit override remains the test/artifact seam.
 */
export function resolveStudioToolRegistrationOptions(
  overrides: StudioToolRegistrationOptions = {},
): StudioToolRegistrationOptions {
  const port = resolveStudioApplicationArtifactPersistencePort()
  return {
    ...overrides,
    ...(overrides.libraryAssistant === undefined
      ? { libraryAssistant: resolveStudioLibraryAssistant() }
      : {}),
    ...(port && overrides.applicationArtifactPersistence === undefined
      ? { applicationArtifactPersistence: port }
      : {}),
  }
}
